// React Hook for Push Notifications
import { useState, useEffect, useCallback } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext.jsx';

// Global subscription state manager to prevent race conditions
let globalSubscriptionState = {
  isAttempting: false,
  lastAttempt: 0,
  attempts: 0,
};

// Timeout to reset stuck global state (30 seconds)
let globalStateTimeout = null;

/**
 * Reset global subscription state (useful for debugging or manual resets)
 */
export function resetGlobalSubscriptionState() {
  globalSubscriptionState = {
    isAttempting: false,
    lastAttempt: 0,
    attempts: 0,
  };

  if (globalStateTimeout) {
    clearTimeout(globalStateTimeout);
    globalStateTimeout = null;
  }

  console.log('[usePushNotifications] Global subscription state reset');
}

/**
 * Get global subscription state for debugging
 */
export function getGlobalSubscriptionState() {
  return { ...globalSubscriptionState };
}

/**
 * Set global subscription state with timeout protection
 */
function setGlobalSubscriptionAttempting(isAttempting) {
  globalSubscriptionState.isAttempting = isAttempting;

  if (isAttempting) {
    globalSubscriptionState.lastAttempt = Date.now();
    globalSubscriptionState.attempts++;

    // Set timeout to reset state if it gets stuck
    if (globalStateTimeout) {
      clearTimeout(globalStateTimeout);
    }
    globalStateTimeout = setTimeout(() => {
      console.warn('[usePushNotifications] Global subscription state timeout, resetting...');
      resetGlobalSubscriptionState();
    }, 30000); // 30 seconds timeout
  } else {
    if (globalStateTimeout) {
      clearTimeout(globalStateTimeout);
      globalStateTimeout = null;
    }
  }
}

/**
 * Custom hook for managing push notifications
 */
export function usePushNotifications() {
  // Delegate to context for core state/actions
  const ctx = useSubscription();
  // Preserve legacy API shape for minimal changes elsewhere
  const getStatus = useCallback(() => ({
    isSupported: ctx.isSupported,
    permission: ctx.permission,
    isSubscribed: ctx.isSubscribed,
    isLoading: ctx.isLoading,
    error: ctx.error,
    subscription: ctx.subscription,
    canRequestPermission: ctx.canRequestPermission,
    canSubscribe: ctx.canSubscribe,
    canUnsubscribe: ctx.canUnsubscribe,
  }), [ctx]);

  return {
    // State
    isSupported: ctx.isSupported,
    permission: ctx.permission,
    isSubscribed: ctx.isSubscribed,
    isLoading: ctx.isLoading,
    error: ctx.error,
    subscription: ctx.subscription,
    isInitialized: ctx.isInitialized,

    // Actions
    requestPermission: ctx.requestPermission,
    subscribe: ctx.subscribe,
    unsubscribe: ctx.unsubscribe,
    requestPermissionAndSubscribe: ctx.requestPermissionAndSubscribe,
    testNotification: () => null,

    // Computed
    canRequestPermission: ctx.canRequestPermission,
    canSubscribe: ctx.canSubscribe,
    canUnsubscribe: ctx.canUnsubscribe,

    // Debug (no-ops preserved for compatibility)
    getDebugInfo: () => ({}),
    getGlobalState: ctx.getGlobalState,
    resetGlobalState: () => { },

    // Legacy helper
    getStatus,
  };
}

/**
 * Hook to automatically request push notification permission after login
 * IMPROVED: Better error handling and subscription persistence
 */
export function useAutoRequestPushPermission(isLoggedIn = false, delay = 2000) {
  const [hasAutoRequested, setHasAutoRequested] = useState(false);
  const { requestPermissionAndSubscribe, isSupported, permission } = usePushNotifications();

  useEffect(() => {
    if (!isLoggedIn || hasAutoRequested || !isSupported) {
      return;
    }

    // Only auto-request if permission is default (not granted or denied)
    if (permission !== 'default') {
      return;
    }

    const timer = setTimeout(async () => {
      try {
        // Double-check that user is still logged in before attempting
        const token = Cookies.get('access_token');
        if (!token) {
          console.warn('[useAutoRequestPushPermission] No auth token found, skipping auto-request');
          return;
        }

        console.log('[useAutoRequestPushPermission] Auto-requesting push permission after login');
        await requestPermissionAndSubscribe();
        setHasAutoRequested(true);
      } catch (error) {
        console.log('[useAutoRequestPushPermission] Auto-request failed:', error.message);
        // Don't mark as attempted if it's an authentication error, as the user might need to log in again
        if (error.response?.status === 401) {
          console.warn('[useAutoRequestPushPermission] Authentication error, will retry on next login');
          return; // Don't set hasAutoRequested to true, so it can retry
        }
        setHasAutoRequested(true); // Mark as attempted for other errors
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [isLoggedIn, hasAutoRequested, isSupported, permission, requestPermissionAndSubscribe, delay]);

  return hasAutoRequested;
}

/**
 * Hook to automatically subscribe to push notifications when permission is granted
 * IMPROVED: Better subscription validation and renewal
 */
export function useAutoSubscribe(enabled = true, delay = 1000) {
  const [hasAutoSubscribed, setHasAutoSubscribed] = useState(false);
  const { subscribe, isSupported, permission, isSubscribed, isLoading, getGlobalState, setGlobalAttempting } = useSubscription();

  useEffect(() => {
    if (!enabled || hasAutoSubscribed || !isSupported) {
      return;
    }

    // Only auto-subscribe if:
    // 1. Permission is granted
    // 2. User is not already subscribed
    // 3. Not currently loading
    // 4. No global subscription attempt in progress
    if (
      permission !== 'granted' ||
      isSubscribed ||
      isLoading ||
      getGlobalState().isAttempting
    ) {
      return;
    }

    const timer = setTimeout(async () => {
      // Check global state again before attempting
      if (getGlobalState().isAttempting) {
        return;
      }

      setGlobalAttempting(true);

      try {
        console.log('[useAutoSubscribe] Auto-subscribing to push notifications');
        await subscribe();
        setHasAutoSubscribed(true);
      } catch (error) {
        console.log('[useAutoSubscribe] Auto-subscription failed:', error.message);
        setHasAutoSubscribed(true); // Mark as attempted even if failed
      } finally {
        setGlobalAttempting(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [
    enabled,
    hasAutoSubscribed,
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    delay,
  ]);

  return hasAutoSubscribed;
}

/**
 * Hook to automatically handle push notification setup
 * IMPROVED: Better subscription persistence and validation
 */
export function useAutoPushSetup(
  isLoggedIn = false,
  autoRequestPermission = true,
  autoSubscribe = true
) {
  const [setupComplete, setSetupComplete] = useState(false);
  const { requestPermission, subscribe, isSupported, permission, isSubscribed, isLoading } =
    usePushNotifications();

  useEffect(() => {
    if (!isLoggedIn || setupComplete || !isSupported) {
      return;
    }

    const handleSetup = async () => {
      try {
        // Step 1: Request permission if needed and auto-request is enabled
        if (autoRequestPermission && permission === 'default') {
          console.log('[useAutoPushSetup] Requesting permission');
          const permissionResult = await requestPermission();

          if (permissionResult !== 'granted') {
            console.log('[useAutoPushSetup] Permission not granted, stopping setup');
            setSetupComplete(true);
            return;
          }
        }

        // Step 2: Subscribe if permission is granted, not subscribed, and auto-subscribe is enabled
        if (autoSubscribe && permission === 'granted' && !isSubscribed && !isLoading) {
          // Check global state before attempting subscription
          if (globalSubscriptionState.isAttempting) {
            console.log('[useAutoPushSetup] Global subscription in progress, skipping');
            setSetupComplete(true);
            return;
          }

          setGlobalSubscriptionAttempting(true);

          try {
            console.log('[useAutoPushSetup] Auto-subscribing to push notifications');
            await subscribe();
          } finally {
            setGlobalSubscriptionAttempting(false);
          }
        }

        setSetupComplete(true);
      } catch (error) {
        console.log('[useAutoPushSetup] Setup failed:', error.message);
        setSetupComplete(true); // Mark as complete even if failed
        setGlobalSubscriptionAttempting(false);
      }
    };

    // Add a delay to ensure the component is fully loaded
    const timer = setTimeout(handleSetup, 2000);
    return () => clearTimeout(timer);
  }, [
    isLoggedIn,
    setupComplete,
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    autoRequestPermission,
    autoSubscribe,
    requestPermission,
    subscribe,
  ]);

  return setupComplete;
}

/**
 * Hook to automatically subscribe to push notifications if not already subscribed
 * IMPROVED: Better subscription validation, renewal, and persistence through updates
 */
export function useAutoSubscribeIfNotSubscribed(isLoggedIn = false, delay = 2000) {
  const [hasAutoSubscribed, setHasAutoSubscribed] = useState(false);
  const [isAttempting, setIsAttempting] = useState(false);
  const {
    requestPermissionAndSubscribe,
    subscribe,
    isSupported,
    permission,
    isSubscribed,
    isLoading,
  } = usePushNotifications();

  useEffect(() => {
    if (!isLoggedIn || hasAutoSubscribed || !isSupported || isAttempting) {
      return;
    }

    // If already subscribed, no need to do anything
    if (isSubscribed) {
      setHasAutoSubscribed(true);
      return;
    }

    const timer = setTimeout(async () => {
      // Prevent multiple simultaneous attempts (both local and global)
      if (isAttempting || globalSubscriptionState.isAttempting) {
        return;
      }

      setIsAttempting(true);
      setGlobalSubscriptionAttempting(true);

      try {
        console.log(
          '[useAutoSubscribeIfNotSubscribed] Checking subscription status and auto-subscribing if needed'
        );



        // Double-check subscription status before attempting to subscribe
        if (isSubscribed) {
          console.log('[useAutoSubscribeIfNotSubscribed] Already subscribed, skipping');
          setHasAutoSubscribed(true);
          return;
        }

        if (permission === 'granted') {
          // Permission already granted, just subscribe
          console.log('[useAutoSubscribeIfNotSubscribed] Permission already granted, subscribing');
          await subscribe();
        } else {
          // Request permission and subscribe in one step
          console.log('[useAutoSubscribeIfNotSubscribed] Requesting permission and subscribing');
          await requestPermissionAndSubscribe();
        }

        setHasAutoSubscribed(true);
      } catch (error) {
        console.log('[useAutoSubscribeIfNotSubscribed] Auto-subscription failed:', error.message);
        setHasAutoSubscribed(true); // Mark as attempted even if failed
      } finally {
        setIsAttempting(false);
        setGlobalSubscriptionAttempting(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [
    isLoggedIn,
    hasAutoSubscribed,
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    isAttempting,
    subscribe,
    requestPermissionAndSubscribe,
    delay,
  ]);

  return hasAutoSubscribed;
}

/**
 * Hook to show subscription banner when subscription is missing or invalid
 * NEW: Automatically shows banner when subscription needs attention
 */
export function useSubscriptionBanner(isLoggedIn = false, delay = 3000) {
  const [shouldShowBanner, setShouldShowBanner] = useState(false);
  const [bannerReason, setBannerReason] = useState('');
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    isInitialized,
  } = usePushNotifications();

  useEffect(() => {
    if (!isLoggedIn || !isSupported || !isInitialized || isLoading) {
      return;
    }

    const timer = setTimeout(() => {
      let reason = '';
      let shouldShow = false;

      // Check if banner should be shown
      if (permission === 'default') {
        reason = 'permission_needed';
        shouldShow = true;
      } else if (permission === 'granted' && !isSubscribed) {
        reason = 'subscription_missing';
        shouldShow = true;
      }

      setBannerReason(reason);
      setShouldShowBanner(shouldShow);
    }, delay);

    return () => clearTimeout(timer);
  }, [isLoggedIn, isSupported, isInitialized, isLoading, permission, isSubscribed, delay]);

  return {
    shouldShowBanner,
    bannerReason,
    isSupported,
    permission,
    isSubscribed,
  };
}
